// Phase B6b — Reverse links: Apostille deep page → matching
// Police-Clearance destination page + Marriage nationality page.
//
// Closes the topical loop so Google/SGE/Perplexity see bidirectional
// edges across the cluster:
//   Marriage(DE) ↔ Apostille(DE) ↔ Police(DE)
//
// Only renders a link if the target route actually exists (validated
// against TARGET_COUNTRIES + a curated nationality allow-list), so it
// never points at a 404. No new routes; pure forward-link.

import { Link } from "@tanstack/react-router";
import { ArrowRight, FileText, ShieldCheck, Heart } from "lucide-react";
import { TARGET_COUNTRIES } from "@/data/police-target-countries";

// Apostille country slug → Police-target country slug.
// Identical slugs resolve automatically; only differences listed.
const APOSTILLE_TO_POLICE: Record<string, string> = {
  uk: "united-kingdom",
};

// Apostille country slug → Marriage nationality slug (demonym).
// Curated to the nationalities that have a /marriage/$nationality page.
const APOSTILLE_TO_NATIONALITY: Record<string, string> = {
  usa: "american",
  canada: "canadian",
  uk: "british",
  germany: "german",
  france: "french",
  italy: "italian",
  spain: "spanish",
  netherlands: "dutch",
  belgium: "belgian",
  switzerland: "swiss",
  sweden: "swedish",
  norway: "norwegian",
  denmark: "danish",
  finland: "finnish",
  austria: "austrian",
  portugal: "portuguese",
  ireland: "irish",
  japan: "japanese",
  "south-korea": "korean",
  china: "chinese",
  india: "indian",
  philippines: "filipino",
  singapore: "singaporean",
  australia: "australian",
  uae: "emirati",
  russia: "russian",
};

export function resolvePoliceSlug(apostilleSlug: string): string | null {
  const candidate = APOSTILLE_TO_POLICE[apostilleSlug] ?? apostilleSlug;
  return TARGET_COUNTRIES.some((c) => c.slug === candidate) ? candidate : null;
}

export function resolveNationalitySlug(apostilleSlug: string): string | null {
  return APOSTILLE_TO_NATIONALITY[apostilleSlug] ?? null;
}

interface Props {
  mode: "th" | "en";
  apostilleSlug: string;
  countryNameTh: string;
  countryNameEn: string;
  isHague: boolean;
}

export default function ApostilleReverseLinks({
  mode, apostilleSlug, countryNameTh, countryNameEn, isHague,
}: Props) {
  const policeSlug = resolvePoliceSlug(apostilleSlug);
  const natSlug = resolveNationalitySlug(apostilleSlug);
  if (!policeSlug && !natSlug) return null;

  const isTh = mode === "th";
  const country = isTh ? countryNameTh : countryNameEn;

  const headlineTh = `เอกสารต้นทางที่มักต้อง ${isHague ? "Apostille" : "Legalization"} คู่กันสำหรับ${country}`;
  const headlineEn = `Source documents commonly ${isHague ? "apostilled" : "legalized"} together for ${country}`;
  const introTh = `Apostille คือขั้นตอน "รับรองตราประทับ" เอกสารที่ออกในไทยให้ใช้ที่${country}ได้ — ขั้นตอนก่อนหน้าคือต้องมี (1) ใบรับรองความประพฤติจาก ตร. และ (2) ใบสำคัญการสมรส คร.2/3 ที่ออกในไทย จึงจะนำมา Apostille ได้`;
  const introEn = `Apostille certifies the seal/signature on a Thai-issued document so ${country} authorities will accept it. Two of the most common source documents are the Thai Police Clearance Certificate (PCSC) and the Thai marriage certificate (Kor.Ror.2/3) — both must be issued first, then routed through MFA for ${isHague ? "Apostille" : "legalization + embassy attestation"}.`;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        {isTh ? "เอกสารต้นทางที่เกี่ยวข้อง" : "Related source documents"}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh ? headlineTh : headlineEn}
      </h2>
      <p className="mt-3 max-w-3xl text-[14.5px] leading-relaxed text-slate-700">
        {isTh ? introTh : introEn}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {policeSlug && (
          <Link
            to={isTh ? "/police-clearance/for/$country" : "/en/police-clearance/for/$country"}
            params={{ country: policeSlug }}
            className="group flex items-start gap-3 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 transition hover:-translate-y-0.5 hover:border-emerald-400 hover:shadow-md"
            data-track-zone="apostille-reverse-link-police"
          >
            <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                {isTh ? "ขั้นตอนต้นทาง #1" : "Source step #1"}
              </div>
              <div className="mt-0.5 font-display text-base font-extrabold text-navy-deep group-hover:text-emerald-700">
                {isTh
                  ? `ใบรับรองความประพฤติไทย (PCSC) สำหรับ${country}`
                  : `Thai Police Clearance (PCSC) for ${country}`}
              </div>
              <div className="mt-1 text-[12px] leading-relaxed text-slate-600">
                {isTh
                  ? "ออกโดยกองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ — เป็นเอกสารต้นทางหลักสำหรับวีซ่าครอบครัว/ทำงาน/PR"
                  : "Issued by the Criminal Records Division, Royal Thai Police — the primary source document for family/work/PR visa files."}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-emerald-700 group-hover:gap-1.5">
                {isTh ? "ดูขั้นตอน PCSC + ราคา" : "View PCSC pipeline + pricing"}
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </Link>
        )}

        {natSlug && (
          <Link
            to={isTh ? "/marriage/$nationality" : "/en/marriage/$nationality"}
            params={{ nationality: natSlug }}
            className="group flex items-start gap-3 rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white p-5 transition hover:-translate-y-0.5 hover:border-rose-400 hover:shadow-md"
            data-track-zone="apostille-reverse-link-marriage"
          >
            <Heart className="mt-0.5 h-6 w-6 shrink-0 text-rose-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">
                {isTh ? "ขั้นตอนต้นทาง #2" : "Source step #2"}
              </div>
              <div className="mt-0.5 font-display text-base font-extrabold text-navy-deep group-hover:text-rose-700">
                {isTh
                  ? `จดทะเบียนสมรสกับชาว${country}ในไทย`
                  : `Marriage registration in Thailand with a ${countryNameEn} national`}
              </div>
              <div className="mt-1 text-[12px] leading-relaxed text-slate-600">
                {isTh
                  ? "ออก คร.2 (ทะเบียนสมรส) + คร.3 (ใบสำคัญฯ) ที่อำเภอ — เอกสารต้นทางสำหรับวีซ่าคู่สมรสและการรับรองสถานะที่ปลายทาง"
                  : "Produces the Kor.Ror.2 (marriage register) and Kor.Ror.3 (marriage certificate) at the Amphur — the source documents for spouse-visa and marital-status recognition abroad."}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-rose-700 group-hover:gap-1.5">
                {isTh ? "ดูขั้นตอนจดทะเบียน" : "View registration pipeline"}
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </Link>
        )}
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] leading-relaxed text-slate-700">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        {isTh
          ? `ลำดับมาตรฐานที่ถูกต้องตามกฎ Hague Apostille Convention (ไทยเข้าร่วม เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570)): ออกเอกสารต้นฉบับ → แปลโดยนักแปลรับรอง (MoJ-listed) → Notary Public → กรมการกงสุล (MFA) ${isHague ? "รับรองนิติกรณ์ (Apostille จะใช้ได้เมื่ออนุสัญญามีผลกับไทย 28 ก.พ. 2570)" : "Legalization + รับรองสถานทูต"}`
          : `Compliant sequence under the Hague Apostille Convention (Thailand acceded 14 Dec 2024): issue the source document → certified translation by an MoJ-listed sworn translator → Notary Public → MFA Department of Consular Affairs ${isHague ? "Apostille" : "legalization + embassy attestation"}.`}
      </div>
    </section>
  );
}
